import saveAs from "file-saver";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAuthStore, usePipedriveStore } from "../..";
import type IBoeModel from "../../interfaces/IBoeModel";
import type IPipedriveOrganization from "../../interfaces/IPipedriveOrganization";
import type IProperty from "../../interfaces/IProperty";
import ReportsApiService from "../../services/reportsApiService";
import usePropertyEstimates, { type IEstimates } from "../usePropertyEstimates";
import { getUserInitials } from "./boeMappings";

export interface IBoeDataSource<T> {
    data: T | undefined;
    isLoading: boolean;
    isError: boolean;
    fetch: () => void;
}

export function useBoeGenerator<TData>(
    property: IProperty | undefined,
    dataSource: IBoeDataSource<TData>,
    buildModel: (
        data: TData,
        estimates: IEstimates,
        initials: string,
        pipedriveOrganizations: IPipedriveOrganization[],
    ) => IBoeModel | null,
): { generateBoe: () => void; isGenerating: boolean } {
    const authStore = useAuthStore();
    const pipedriveStore = usePipedriveStore();
    const reportsApiService = useMemo(() => new ReportsApiService(), []);
    const estimates = usePropertyEstimates(property);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        setIsGenerating(false);
    }, [property?.id, buildModel]);

    useEffect(() => {
        if (isGenerating && dataSource.isError) {
            toast.error("Failed to generate BOE data");
            setIsGenerating(false);
        }
    }, [isGenerating, dataSource]);

    useEffect(() => {
        if (!property) return;

        if (isGenerating && !dataSource.data && !dataSource.isLoading && !dataSource.isError) {
            dataSource.fetch();
        }
    }, [property, isGenerating, dataSource]);

    const generateInternal = useCallback(async (currentData: TData) => {
        if (!property) {
            setIsGenerating(false);
            return;
        }

        try {
            const initials = getUserInitials(authStore.user);
            const model = buildModel(currentData, estimates, initials, pipedriveStore.organizations);
            if (!model) return;

            const file = await reportsApiService.boe(model);
            saveAs(file.data, file.name);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : String(err));
        } finally {
            setIsGenerating(false);
        }
    }, [authStore.user, buildModel, estimates, pipedriveStore.organizations, property, reportsApiService]);

    useEffect(() => {
        if (isGenerating && dataSource.data && property) {
            generateInternal(dataSource.data);
        }
    }, [isGenerating, dataSource.data, property, generateInternal]);

    const generateBoe = useCallback(() => {
        if (!property) return;

        if (!dataSource.data && !dataSource.isLoading && !dataSource.isError) {
            dataSource.fetch();
        }

        setIsGenerating(true);
    }, [property, dataSource]);

    return { generateBoe, isGenerating };
}
